// Renders the canonical dashboard guide (docs/dashboard-guide.html) inside a style-isolated iframe
// so its standalone document CSS (body/section/table/…) can't leak into the app. The HTML is
// imported raw at build time, so this stays a single source of truth — no copy to keep in sync.
// Anchors inside a srcdoc frame would navigate the frame to the app (see guideDocument.ts), so
// both documents get a small click interceptor before they are handed to the frame.
// When the Operations feature is on, a second document is offered the same way: the card-by-card
// Operations guide (docs/operations-cards-explained.html), written for the people running the scrum.
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useFeatureFlags } from "@/config/useFeatureFlags";
import guideHtml from "../../../../../docs/dashboard-guide.html?raw";
import operationsGuideHtml from "../../../../../docs/operations-cards-explained.html?raw";
import { prepareGuideDocument } from "./guideDocument";

// Sections of the guide wrapped in <!-- THROUGHPUT:START -->…<!-- THROUGHPUT:END --> are only
// relevant when the Throughput feature is on; strip them otherwise so the guide matches the UI.
const THROUGHPUT_BLOCK = /<!-- THROUGHPUT:START -->[\s\S]*?<!-- THROUGHPUT:END -->/g;
// Same treatment for the Operations dashboard: <!-- OPERATIONS:START -->…<!-- OPERATIONS:END -->.
const OPERATIONS_BLOCK = /<!-- OPERATIONS:START -->[\s\S]*?<!-- OPERATIONS:END -->/g;

/** The documents the Guide tab can show; `doc=` in the URL picks one so a view can be linked. */
export type GuideDocument = "guide" | "operations";
const DOC_PARAM = "doc";

interface GuideDocumentDefinition {
  key: GuideDocument;
  label: string;
}

const DOCUMENTS: readonly GuideDocumentDefinition[] = [
  { key: "guide", label: "Dashboard guide" },
  { key: "operations", label: "Operations, card by card" },
];

export function InfoScreen() {
  const flags = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();

  // Flag off: only the main guide exists, whatever the URL says.
  const requested = searchParams.get(DOC_PARAM);
  const doc: GuideDocument = flags.operations && requested === "operations" ? "operations" : "guide";

  const html = useMemo(() => {
    if (doc === "operations") return prepareGuideDocument(operationsGuideHtml);
    let out = guideHtml;
    if (!flags.throughput) out = out.replace(THROUGHPUT_BLOCK, "");
    if (!flags.operations) out = out.replace(OPERATIONS_BLOCK, "");
    return prepareGuideDocument(out);
  }, [doc, flags.throughput, flags.operations]);

  const select = (next: GuideDocument) => {
    const params = new URLSearchParams(searchParams);
    if (next === "guide") params.delete(DOC_PARAM);
    else params.set(DOC_PARAM, next);
    setSearchParams(params, { replace: true });
  };

  const title = DOCUMENTS.find((d) => d.key === doc)?.label ?? "Dashboard Guide";

  return (
    <main className="info-screen">
      {flags.operations && (
        <div className="info-tabs unit-toggle" role="tablist" aria-label="Guide document">
          {DOCUMENTS.map((d) => (
            <button
              key={d.key}
              type="button"
              role="tab"
              aria-selected={doc === d.key}
              className={doc === d.key ? "on" : ""}
              onClick={() => select(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
      <iframe title={title} className="info-frame" srcDoc={html} />
    </main>
  );
}
